import { useCallback } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { paths } from '../../app/routes'
import { useToast } from '../../components/feedback/toastContext'
import { CameraViewfinder } from '../../components/scanner/CameraViewfinder'
import { ScanSimulator } from '../../components/scanner/ScanSimulator'
import { Button } from '../../components/ui/Button'
import { triggerFeedback } from '../../feedback/feedback'
import { useDeviceSettings } from '../../feedback/settings'
import { faNumber } from '../../lib/faNumbers'
import { addToBasket, undoBasketLast } from '../../outbox/operations'
import { db } from '../../outbox/db'
import { useBarcodeScanner } from '../../scanner/useBarcodeScanner'
import { useTorch } from '../../scanner/useTorch'
import styles from './BasketScreen.module.css'

const SERIAL_CODE = /^SN-[A-Za-z0-9-]+$/

export function BasketScreen() {
  const navigate = useNavigate()
  const { show } = useToast()
  const { settings } = useDeviceSettings()
  const entries =
    useLiveQuery(() => db.basket.orderBy('seq').reverse().toArray(), [], []) ?? []

  const record = useCallback(
    async (serialNumber: string, captureMethod: 'camera' | 'manual') => {
      const existing = await db.basket.get(serialNumber)

      if (existing !== undefined) {
        triggerFeedback('rejected')
        show('این سریال در سبد موجود است', 'warn')
        return
      }

      await addToBasket(serialNumber, captureMethod)
      triggerFeedback('accepted')
    },
    [show],
  )

  const onDetect = useCallback(
    (value: string) => {
      const code = value.trim().toUpperCase()

      if (!SERIAL_CODE.test(code)) {
        triggerFeedback('rejected')
        show('این بارکد سریال نیست.', 'warn')
        return
      }

      void record(code, 'camera')
    },
    [record, show],
  )

  const scanner = useBarcodeScanner({
    enabled: true,
    onDetect,
    region: { width: 0.85, height: 0.5 },
  })
  useTorch(scanner.track, settings.torch)

  const onUndo = async () => {
    const removed = await undoBasketLast()

    if (removed === null) {
      show('اسکنی برای لغو وجود ندارد', 'warn')
      return
    }

    show('آخرین اسکن حذف شد', 'ok')
  }

  const removeEntry = async (serialNumber: string) => {
    await db.basket.delete(serialNumber)
    show('سریال از سبد حذف شد', 'ok')
  }

  const clearBasket = async () => {
    await db.basket.clear()
    show('سبد خالی شد', 'ok')
  }

  const goAssign = () => {
    if (entries.length === 0) {
      show('سبد خالی است', 'warn')
      return
    }

    navigate(paths.assign)
  }

  const lastEntry = entries[0]

  return (
    <div className={styles.screen}>
      <div className={styles.count}>
        <div className={styles.countValue}>{faNumber(entries.length)}</div>
        <div className={styles.countLabel}>عدد آماده تخصیص</div>
      </div>

      <CameraViewfinder
        size="basket"
        badgeLabel="اسکن پیوسته فعال"
        hint="سریال‌ها را پشت‌سرهم اسکن کنید"
        status={scanner.status}
        error={scanner.error}
        videoRef={scanner.videoRef}
        onActivate={scanner.retry}
        showBadge={false}
      />

      {lastEntry !== undefined && (
        <div className={styles.last}>
          <div className={styles.lastCopy}>
            <div className={styles.lastTitle}>✓ افزوده شد</div>
            <div className={styles.lastSerial} dir="ltr">
              {lastEntry.serialNumber}
            </div>
          </div>
          <button type="button" className={styles.undo} onClick={() => void onUndo()}>
            لغو
          </button>
        </div>
      )}

      {import.meta.env.DEV && (
        <ScanSimulator onScan={onDetect} duplicateOf={lastEntry?.serialNumber} />
      )}

      <div className={`${styles.items} scroll-y`}>
        {entries.length === 0 ? (
          <div className={styles.empty}>سبد خالی است.</div>
        ) : (
          entries.map((entry, index) => (
            <div key={entry.serialNumber} className={styles.row}>
              <span className={styles.index} dir="ltr">
                {faNumber(entries.length - index)}
              </span>
              <span className={styles.serial} dir="ltr">
                {entry.serialNumber}
              </span>
              <button
                type="button"
                className={styles.remove}
                onClick={() => void removeEntry(entry.serialNumber)}
                aria-label={`حذف ${entry.serialNumber}`}
              >
                حذف
              </button>
            </div>
          ))
        )}
      </div>

      <div className={styles.actions}>
        <Button
          variant="secondary"
          compact
          className={styles.clearButton}
          onClick={() => void clearBasket()}
        >
          خالی کن
        </Button>
        <Button compact onClick={goAssign}>
          تخصیص به پالت
        </Button>
      </div>
    </div>
  )
}
